/**
 * @nanokart/tryon-studio-sdk
 * Shopify Host Adapter
 * 
 * Adapter for Shopify theme embeds.
 * Uses Shopify Cart API and Analytics.
 */

import { DefaultHostAdapter } from './DefaultHostAdapter';
import { DefaultHostAdapter } from './DefaultHostAdapter';
import { AddToCartIntent, HostAdapterCallbacks, HostEnvironment } from './types';
import { AnalyticsEvent } from '../../types/events';

// Shopify global type augmentation
interface ShopifyWindow extends Window {
  ShopifyAnalytics?: {
    lib: { track: (name: string, properties: Record<string, unknown>) => void };
  };
  Shopify?: {
    currency?: { active: string };
    locale?: string;
  };
}

export class ShopifyHostAdapter extends DefaultHostAdapter {
  constructor(callbacks: HostAdapterCallbacks = {}) {
    super(callbacks);
  }

  async addToCart(intent: AddToCartIntent): Promise<void> {
    // Shopify Cart API
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: intent.variantId,
          quantity: intent.quantity,
          properties: {
            _nanokart_tryon: 'true',
            _tryon_image: intent.metadata?.tryonImageUrl ?? '',
            _moment_type: intent.metadata?.momentType ?? '',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Shopify cart error: ${response.status}`);
      }

      // Notify Shopify UI to update cart
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      console.error('[ShopifyHostAdapter] Failed to add to cart:', error);
      throw error;
    }

    // Call parent for callbacks
    await super.addToCart(intent);
  }

  async removeFromCart(productId: string, variantId?: string): Promise<void> {
    // Shopify Cart API - set quantity to 0
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 0,
        }),
      });

      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      console.error('[ShopifyHostAdapter] Failed to remove from cart:', error);
    }

    await super.removeFromCart(productId, variantId);
  }

  track(event: AnalyticsEvent): void {
    // Shopify Analytics
    if (typeof window !== 'undefined' && (window as unknown as ShopifyWindow).ShopifyAnalytics) {
      (window as unknown as ShopifyWindow).ShopifyAnalytics!.lib.track(event.payload.name as string, event.payload.properties as Record<string, unknown>);
    }

    super.track(event);
  }

  openCheckout(): void {
    // No-op: Shopify owns checkout
    console.warn('[ShopifyHostAdapter] openCheckout called but Shopify owns checkout');
  }

  getHostEnv(): HostEnvironment {
    const baseEnv = super.getHostEnv();

    // Enhance with Shopify-specific info
    return {
      ...baseEnv,
      currency: (window as unknown as ShopifyWindow).Shopify?.currency?.active ?? baseEnv.currency,
      locale: (window as unknown as ShopifyWindow).Shopify?.locale ?? baseEnv.locale,
    };
  }
}
